import { useState, useEffect, useRef } from 'react';
import styles from '../assets/styles/card.module.css'
import Backarrow from '../components/Backarrow';
import Toast from '../lib/toast';
import { playErrorSound, playStartSound } from '../lib/audio';

function Card({currentTab, setCurrentTab, eventPath, robotNameList, funnySounds, sfxEnabled}) {
    const [started, setStarted] = useState(false);
    const [question, setQuestion] = useState(null);
    const [options, setOptions] = useState([]);
    const [correct, setCorrect] = useState(null);
    const [result, setResult] = useState(null);
    const [locked, setLocked] = useState(false);
    const [streak, setStreak] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [bestStreak, setBestStreak] = useState(0);
    const [fadingOut, setFadingOut] = useState(false);
    const [lastCorrect, setLastCorrect] = useState(null);
    const [lastTeam, setLastTeam] = useState(null);
    const difficultyList = ["easy", "medium", "hard"]
    const [difficulty, setDifficulty] = useState(difficultyList[0]);
    const [inputGuess, setInputGuess] = useState("");
    const inputRef = useRef(null);
    const [maxTime, setMaxTime] = useState(10000)
    const [timeLeft, setTimeLeft] = useState(maxTime);
    const [message, setMessage] = useState("nice try")
    const waitTime = 1200; //time before question gets generated in ms
    const messageOption = [
        "better luck next time", 
        "nice try"
    ]
    
    /**
     * Generates random robot question plus 3 random wrong answers
     * @returns 
     */
    function generateQuestion(){
        if (!robotNameList || robotNameList.size === 0) return;

        const entries = Array.from(robotNameList.entries());
        const random = entries[Math.floor(Math.random() * entries.length)];
        const [teamNum, teamName] = random;

        if (difficulty === difficultyList[2]) {
            // HARD MODE: show name, expect number
            setQuestion(teamName);
            setCorrect(teamNum.toString());
            setOptions([]);
        } else {
            //EASY/MEDIUYMN MODE: MCQ SHOWING THE NUMBER, GUESSING THE NAME
            // picks 3 wrong answers from the bank
            const shuffled = [...entries].sort(() => 0.5 - Math.random());

            const wrong = shuffled
                .filter(([num]) => num !== teamNum)
                .slice(0, 3)
                .map(e => e[1]);

            const allOptions = [...wrong, teamName].sort(() => 0.5 - Math.random());

            setQuestion(teamNum);
            setOptions(allOptions);
            setCorrect(teamName);
        }
        setResult(null);
        setLocked(false);
        setTimeLeft(maxTime);
        setInputGuess("");
    }

    function startGame() {
        if (!eventPath){ Toast.error("No event path selected", funnySounds, sfxEnabled); return;}
        if (!robotNameList || robotNameList.size === 0) { Toast.error("No robot list → check event path", funnySounds, sfxEnabled); return;}
        let newTime = 5000;
        switch (difficulty){
            case difficultyList[0]:
                newTime = (7000)
                break;
            case difficultyList[1]:
                newTime = (2500)
                break;
            default:
                newTime = (3500)
                break;
        }
        
        setTimeLeft(newTime);
        setMaxTime(newTime);
        setStarted(true);
        setInputGuess("");
        generateQuestion();
        playStartSound(funnySounds, sfxEnabled)
    }

    function exitGame() {
        setStarted(false);
        setGameOver(false);
        setQuestion(null);
        setOptions([]);
        setCorrect(null);
        setResult(null);
        setLocked(false);
        setStreak(0);
    }

    function replayGame() {
        setStreak(0);
        setGameOver(false);
        startGame();
    }

    /**
     * Handler for when user submits an answer / clicks an mcq button
     * @param {*} choice 
     * @returns 
     */
    function handleGuess(choice) {
        if (locked) return;
        let isMCQ = true;
        if(difficulty === difficultyList[2]){
            isMCQ = false;
        }
        const guess = isMCQ ? choice : inputGuess.toString().trim();
        const isCorrect = guess !== null && guess === correct.toString();
        setLocked(true);

        if (isCorrect) {
            setStreak(prev => {
                const newStreak = prev + 1;
                setBestStreak(best => Math.max(best, newStreak));
                return newStreak;
            });

            setResult("Correct!");

            setTimeout(() => {
                generateQuestion()
            }, waitTime);

        } else {
            setResult(`Wrong! Answer: ${correct}`);
            setMessage(messageOption[Math.floor(Math.random() * messageOption.length)])
            setLastCorrect(correct);
            setLastTeam(question);
            playErrorSound(funnySounds, sfxEnabled);
            setFadingOut(true);
            setTimeout(() => {
                setGameOver(true);
                setFadingOut(false);
            }, waitTime);
        }
    }

    /**
     * Exits the game when leaving the page or eventpath changes
     */
    useEffect(() => {
        exitGame()
    }, [currentTab, robotNameList]);

    /**
     * Resets best streak when eventpath changes
     */
    useEffect(() => {
        setBestStreak(0)
    }, [robotNameList, eventPath]);

    /**
     * Handles keypress for submitting answer
     */
    useEffect(() => {
        if(currentTab !== "card") return;
        const handleKeyDown = (e) => {
            if (e.key === "Enter") {
                if(difficulty === difficultyList[2] && inputGuess !== "" && !locked){
                    e.preventDefault();
                    handleGuess(inputGuess);
                }
            }
        }
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        }
    }, [difficulty, locked, inputGuess]);

    /** focuses on input when new question loads */
    useEffect(() => {
        if (started && !gameOver && !locked && difficulty === difficultyList[2]) {
            inputRef.current?.focus();
        }
    }, [question, started, gameOver, locked, difficulty]);

    /**
     * Timer countdown useeffect
     */
    useEffect(() => {
        if (!started || locked || gameOver) return;
        let start = performance.now();
        let rafId;

        const tick = (now) => {
            const elapsed = now - start;
            const remaining = Math.max(maxTime - elapsed, 0);

            setTimeLeft(remaining);

            if (remaining <= 0) {
                handleGuess(null);
                return;
            }

            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [started, locked, gameOver, maxTime]);

    if (currentTab === "card") {
        return (
            <div className="subpage cardPage">
                <div className={styles.cardGrid}>
                    <div className={styles.cardTitle}>Robot Name Quiz</div>
                    <div
                        className={`
                            ${styles.cardGameBox} 
                            ${fadingOut ? styles.fadeOut : ""} 
                            ${gameOver || started ? styles.fadeIn : ""}
                        `}
                    >
                        {!started ? (
                            <div className={styles.startMenu}>
                                <button className={styles.cardNextBtn} onClick={startGame}>
                                    Start Game
                                </button>
                                <div className={styles.difficultySelector}>
                                    {difficultyList.map((dif) => (
                                        <label key={dif}>
                                            <input
                                                type="radio"
                                                name="difficulty"
                                                checked={difficulty === dif}
                                                onChange={(e) => setDifficulty(dif)}
                                            />
                                            <span>{dif}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ) : gameOver ? (
                            <>
                                <div className={styles.cardStats}>
                                    <span>Final Streak: {streak}</span>
                                    <span>Best: {bestStreak}</span>
                                </div>

                                <h2 className={styles.cardQuestion}>Game Over</h2>
                                <div className={styles.postgameMessages}>
                                    <span><i>{message}</i></span>
                                    <span className={styles.cardResult}>
                                        You failed after getting {streak} correct.
                                    </span>
                                    <span className={styles.cardResult}>
                                        Team {lastTeam} was actually: {lastCorrect}
                                    </span>
                                </div>
                                <div className={styles.cardEndButtons}>
                                    <button className={styles.cardNextBtn} onClick={replayGame}>
                                        Play Again
                                    </button>

                                    <button className={styles.cardExitBtn} onClick={exitGame}>
                                        Exit
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.timerBar}>
                                    <div
                                        className={styles.timerFill}
                                        style={{
                                            transform: `scaleX(${timeLeft / maxTime})`,
                                            backgroundColor: timeLeft <= (maxTime/3.5) ? "#c62828" : "var(--mainColor)"                                                
                                        }}
                                    />
                                </div>
                                <div className={styles.cardStats}>
                                    <span>Streak: {streak}</span>
                                    <span>Best: {bestStreak}</span>
                                </div>

                                <h2 className={styles.cardQuestion}>
                                    {difficulty === difficultyList[2] ? question : `Team ${question}`}
                                </h2>

                                {difficulty === difficultyList[2] ? (
                                    <div className={styles.cardOptions}>
                                        <input
                                            ref={inputRef}
                                            type="number"
                                            inputmode="numeric"
                                            placeholder="Enter team number"
                                            value={inputGuess}

                                            onChange={(e) => setInputGuess(e.target.value)}
                                            disabled={locked}
                                            className={styles.cardOptionBtn}
                                        />
                                        <button
                                            className={styles.cardNextBtn}
                                            onClick={() => handleGuess(inputGuess)}
                                            disabled={locked || inputGuess.trim() === ""}
                                        >
                                            Submit
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.cardOptions}>
                                        {options.map((opt, i) => (
                                            <button
                                                key={i}
                                                className={`${styles.cardOptionBtn} ${
                                                    locked
                                                        ? opt === correct
                                                            ? styles.correct
                                                            : result && opt !== correct
                                                            ? styles.wrong
                                                            : ""
                                                        : ""
                                                }`}
                                                onClick={() => handleGuess(opt)}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <p className={styles.cardResult}>{result || ""}</p>
                            </>
                        )}
                    </div>
                </div>

                <Backarrow setCurrentTab={setCurrentTab} />
            </div>
        );
    }
}

export default Card;